import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { logout as localLogout } from '../lib/localAuth';
import { useThemeMode } from '../ThemeContext';
import {
  Settings,
  LayoutDashboard as Dashboard,
  Globe as TravelExplore,
  Bot,
  LogOut,
  Moon,
  Sun,
  Plus as Add,
  Edit,
  BugIcon,
  ShieldAlert,
  Brain,
  Trash2 as Remove,
  Menu,
  Activity,
  ClipboardList,
  Boxes,
  Layers,
  X,
  TrendingUp,
  BarChart2,
  BookOpen,
  ScrollText,
  ChevronDown,
  ChevronUp,
  HeartPulse,
  Cpu,
  FlaskConical,
  GraduationCap,
} from 'lucide-react';
import { APP_VERSION, loadChangelog, ChangelogEntry } from '../version';
import NotificationCenter from './NotificationCenter';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useEquitiesMode, useExecutionFocusNav } from '../hooks/useEquitiesMode';


interface NavMenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
  /** Highlight when URL is under this prefix (e.g. `/trade/crypto`). */
  activePathPrefix?: string;
}

const getChangeTypeInfo = (type: ChangelogEntry['changes'][0]['type']) => {
  switch (type) {
    case 'added':
      return { icon: <Add className="w-4 h-4" />, color: 'text-green-500 bg-green-500/10', label: 'Added' };
    case 'changed':
      return { icon: <Edit className="w-4 h-4" />, color: 'text-teal-500 bg-teal-500/10', label: 'Changed' };
    case 'fixed':
      return { icon: <BugIcon className="w-4 h-4" />, color: 'text-yellow-500 bg-yellow-500/10', label: 'Fixed' };
    case 'security':
      return { icon: <ShieldAlert className="w-4 h-4" />, color: 'text-red-500 bg-red-500/10', label: 'Security' };
    case 'removed':
      return { icon: <Remove className="w-4 h-4" />, color: 'text-gray-500 bg-gray-500/10', label: 'Removed' };
    default:
      return { icon: <Edit className="w-4 h-4" />, color: 'text-gray-500 bg-gray-500/10', label: 'Other' };
  }
};

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const { currentUser } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = mode === 'dark';
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [loadingChangelog, setLoadingChangelog] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [tradingToolsOpen, setTradingToolsOpen] = useState(false);
  const equitiesMode = useEquitiesMode();
  const executionFocus = useExecutionFocusNav();
  const [searchParams] = useSearchParams();
  const showResearchTools =
    searchParams.get('tools') === '1' || userRole === 'Admin' || !executionFocus;

  // Load changelog lazily when dialog opens
  useEffect(() => {
    if (changelogOpen && changelog.length === 0) {
      setLoadingChangelog(true);
      loadChangelog()
        .then(setChangelog)
        .finally(() => setLoadingChangelog(false));
    }
  }, [changelogOpen, changelog.length]);

  // Fetch user role for conditional nav items
  useEffect(() => {
    if (currentUser) {
      currentUser.getIdTokenResult().then(result => {
        setUserRole((result.claims.role as string) || 'Trader');
      }).catch(() => setUserRole('Trader'));
    }
  }, [currentUser]);

  // Close mobile sidebar on route change
  useEffect(() => {
    onMobileClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentUser) return null;

  const handleLogout = async () => {
    try {
      localLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const portfolioMenuItems: NavMenuItem[] = [
    { text: 'Overview', icon: <Dashboard className="w-5 h-5" />, path: '/portfolio' },
  ];

  const tradingCoreItems: NavMenuItem[] = [
    {
      text: 'Trade',
      icon: <TrendingUp className="w-5 h-5" />,
      path: '/trade/stocks/overview',
      activePathPrefix: '/trade/stocks',
    },
    { text: 'Fleet Observability', icon: <Bot className="w-5 h-5" />, path: '/fleet' },
    { text: 'Universe', icon: <TravelExplore className="w-5 h-5" />, path: '/universe' },
    { text: 'Decisions', icon: <Activity className="w-5 h-5" />, path: '/decisions' },
    { text: 'Reports', icon: <BarChart2 className="w-5 h-5" />, path: '/reports' },
    { text: 'Day Review', icon: <ScrollText className="w-5 h-5" />, path: '/day-review' },
    { text: 'AI Learning', icon: <GraduationCap className="w-5 h-5" />, path: '/ai-learning' },
    {
      text: 'Transactions',
      icon: <ScrollText className="w-5 h-5" />,
      path: '/monitoring/transactions',
      activePathPrefix: '/monitoring/transactions',
    },
    { text: 'Signals', icon: <Brain className="w-5 h-5" />, path: '/signals', activePathPrefix: '/signals' },
    {
      text: 'Strategy Lab',
      icon: <FlaskConical className="w-5 h-5" />,
      path: '/strategy-lab/watchlists',
      activePathPrefix: '/strategy-lab',
    },
  ];

  const tradingMoreItems: NavMenuItem[] = showResearchTools
    ? [
        { text: 'War Room audit', icon: <ClipboardList className="w-5 h-5" />, path: '/war-room/audit' },
      ]
    : [];

  const systemMenuItems: NavMenuItem[] = [
    { text: 'Platform Health', icon: <HeartPulse className="w-5 h-5" />, path: '/health' },
    ...(userRole === 'Admin'
      ? [
          { text: 'Hermes Agent', icon: <Cpu className="w-5 h-5" />, path: '/hermes' },
          { text: 'Bug Reports', icon: <BugIcon className="w-5 h-5" />, path: '/admin/bugs' },
        ]
      : []),
    { text: 'Settings', icon: <Settings className="w-5 h-5" />, path: '/settings' },
    ...(userRole === 'Admin' ? [{ text: 'Admin', icon: <ShieldAlert className="w-5 h-5" />, path: '/admin' }] : []),
  ];

  const NavItem = ({ item }: { item: NavMenuItem }) => {
    const active = item.activePathPrefix
      ? location.pathname === item.activePathPrefix || location.pathname.startsWith(`${item.activePathPrefix}/`)
      : location.pathname === item.path;
    return (
      <li>
        <Link
          to={item.path}
          className={`group flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-150 ${
            active
              ? 'bg-card text-foreground font-semibold shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-card/60 font-medium'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className={`${active ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'} transition-colors`}>
              {item.icon}
            </span>
            <span className="text-[13px]">{item.text}</span>
          </div>
          {item.badge && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
              {item.badge}
            </Badge>
          )}
        </Link>
      </li>
    );
  };

  const NavSection = ({ label, items }: { label: string; items: NavMenuItem[] }) => (
    <div>
      <span className="block px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2">
        {label}
      </span>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <NavItem key={item.text} item={item} />
        ))}
      </ul>
    </div>
  );

  const userInitials = currentUser.email
    ? currentUser.email.substring(0, 2).toUpperCase()
    : 'U';

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-foreground flex items-center justify-center">
            <span className="text-background font-bold tracking-tighter text-sm">TX</span>
          </div>
          <div className="flex flex-col">
            <span className="font-display font-bold text-lg leading-tight text-card-foreground">Titan-X</span>
            <span className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-widest">Trading Platform</span>
          </div>
        </div>
        {/* Mobile close button */}
        <button
          onClick={onMobileClose}
          className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-2 space-y-5">
        <NavSection label="Portfolio" items={portfolioMenuItems} />
        <div>
          <span className="block px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2">
            {executionFocus ? 'Execution' : 'Trading'}
          </span>
          <ul className="space-y-0.5">
            {tradingCoreItems.map((item) => (
              <NavItem key={item.text} item={item} />
            ))}
          </ul>
          {tradingMoreItems.length > 0 ? (
          <button
            type="button"
            onClick={() => setTradingToolsOpen((o) => !o)}
            className="mt-1.5 mx-2 flex w-[calc(100%-1rem)] items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-card/60 hover:text-foreground transition-colors"
          >
            <span>{executionFocus ? 'Research tools' : 'Analysis tools'}</span>
            {tradingToolsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          ) : null}
          {tradingMoreItems.length > 0 && tradingToolsOpen ? (
            <ul className="mt-1 space-y-0.5 border-l border-border/50 ml-4 pl-2">
              {tradingMoreItems.map((item) => (
                <NavItem key={item.text} item={item} />
              ))}
            </ul>
          ) : null}
        </div>
        <NavSection label="System" items={systemMenuItems} />
      </div>

      {/* Footer */}
      <div className="px-3 pb-3 pt-2 space-y-2.5">
        {/* Notification Center */}
        <div className="flex justify-center">
           <NotificationCenter />
        </div>

        {/* Theme Toggle */}
        <div className="flex rounded-lg bg-card/60 p-0.5">
          <button
            title="Light mode"
            onClick={() => !isDark || toggleTheme()}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${!isDark ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Sun className="w-3.5 h-3.5" />
            <span>Light</span>
          </button>
          <button
            title="Dark mode"
            onClick={() => isDark || toggleTheme()}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all ${isDark ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Moon className="w-3.5 h-3.5" />
            <span>Dark</span>
          </button>
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-2.5 p-2 rounded-lg">
          <div className="w-8 h-8 rounded-full bg-foreground/10 flex items-center justify-center text-foreground font-semibold text-xs flex-shrink-0">
            {userInitials}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-sm font-medium truncate text-foreground">
              {currentUser.email?.split('@')[0] || 'User'}
            </span>
            <button
              onClick={() => setChangelogOpen(true)}
              className="text-[10px] text-left text-muted-foreground hover:text-primary transition-colors font-medium truncate"
            >
              v{APP_VERSION}
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors flex-shrink-0"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar - always visible */}
      <aside className="hidden md:flex w-[240px] flex-shrink-0 h-screen sticky top-0 bg-background flex-col">
        {sidebarContent}
      </aside>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 animate-fade-in"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <aside className="absolute inset-y-0 left-0 w-[280px] bg-background shadow-xl animate-slide-in-left">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Changelog Dialog */}
      <Dialog open={changelogOpen} onOpenChange={setChangelogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Titan-X Changelog</span>
              <span className="text-sm font-normal text-muted-foreground">v{APP_VERSION}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto scrollbar-thin space-y-4 pr-2">
            {loadingChangelog ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              changelog.map((release, index) => (
                <div key={release.version} className={`pb-4 ${index < changelog.length - 1 ? 'border-b' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={release.version === APP_VERSION ? "default" : "secondary"}>
                      v{release.version}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{release.date}</span>
                  </div>
                  <h4 className="font-semibold text-sm mb-3">{release.title}</h4>
                  <ul className="space-y-2">
                    {release.changes.map((change, changeIndex) => {
                      const typeInfo = getChangeTypeInfo(change.type);
                      return (
                        <li key={changeIndex} className="flex gap-2 text-sm text-muted-foreground">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium mt-0.5 flex-shrink-0 ${typeInfo.color}`}>
                            {typeInfo.icon}
                            {typeInfo.label}
                          </span>
                          <span className="leading-tight">{change.description}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Mobile top bar - shown only on small screens when sidebar is hidden */
export const MobileHeader: React.FC<{ onMenuClick: () => void }> = ({ onMenuClick }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return null;

  return (
    <header className="md:hidden sticky top-0 z-40 flex items-center justify-between h-14 px-4 border-b bg-background">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card transition-colors"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-foreground flex items-center justify-center">
            <span className="text-background font-bold text-xs">TX</span>
          </div>
          <span className="font-display font-bold text-base text-foreground">Titan-X</span>
        </div>
      </div>
      <NotificationCenter />
    </header>
  );
};

export default Sidebar;
